import { computed, Injectable, signal } from '@angular/core'

import { UserRole } from '@yoshu-lib/constants/roles.enum'

import { User } from '@yoshu-lib/models/user.model'

const USER_DATA_KEY = 'yoshu_user_data'

/**
 * The `AuthStore` class is responsible for managing the authentication state of the application.
 * It provides reactive signals for the current user and login status, allowing components to
 * respond to changes in authentication state.
 *
 * User data is persisted to localStorage for fast session recovery.
 * Tokens (access and refresh) are stored in HttpOnly cookies managed by the backend.
 */
@Injectable({ providedIn: 'root' })
export class AuthStore {
  private user = signal<User | null>(null)
  readonly role = signal<UserRole | null>(null)

  readonly currentUser = computed(() => this.user())

  setUser(user: User | null) {
    this.user.set(user)

    if (user) {
      this.role.set(user.role)
      this.persistUserData(user)
    } else {
      this.clearPersistedUserData()
    }
  }

  readonly isAdmin = computed(() => this.role() === UserRole.Admin)
  readonly isLoggedIn = computed(() => !!this.user())
  readonly isYoshuStaff = computed(
    () => this.user()?.email.toLowerCase().endsWith('@yoshu.ai') ?? false
  )

  private isLoggingIn = signal(false)

  setIsLoggingIn(value: boolean) {
    this.isLoggingIn.set(value)
  }

  getIsLoggingIn() {
    return this.isLoggingIn()
  }

  getUserLanguage() {
    return this.user()?.language
  }

  clearAuth() {
    this.user.set(null)
    this.role.set(null)
    this.clearPersistedUserData()
  }

  hasStoredSession(): boolean {
    return this.getStoredUserData() !== null
  }

  getStoredUserData(): User | null {
    try {
      const stored = localStorage.getItem(USER_DATA_KEY)
      return stored ? JSON.parse(stored) : null
    } catch {
      return null
    }
  }

  restoreSessionFromStorage(): boolean {
    const userData = this.getStoredUserData()

    if (userData) {
      this.user.set(userData)
      this.role.set(userData.role)
      return true
    }
    return false
  }

  private persistUserData(user: User) {
    try {
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(user))
    } catch {
      console.warn('Failed to persist user data to localStorage')
    }
  }

  private clearPersistedUserData() {
    try {
      localStorage.removeItem(USER_DATA_KEY)
    } catch {
      // Ignore errors
    }
  }
}
